import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, Filter, FileSpreadsheet, X } from 'lucide-react';
import { useFraudFlags } from '../../../hooks/useApi';
import { FraudFlag, FraudFlagFilters, FraudFlagType, FraudFlagSeverity } from '../../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { SortableTableHead } from '@kioskgaming/ui';
import Badge from '../../ui/Badge';
import ExportConfirmModal from '../../ui/ExportConfirmModal';
import { format } from 'date-fns';
import ResolveFraudFlagModal from './ResolveFraudFlagModal';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { apiClient } from '../../../services/api';
import { exportToExcel, cn } from '../../../utils';
import { toast } from 'react-hot-toast';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';

const EXPORT_LIMIT = 1000;

interface FraudFlagsListProps {
  // KIOSK-720: platform_admin is read-only — cannot resolve fraud flags.
  canResolve?: boolean;
}

const FraudFlagsList: React.FC<FraudFlagsListProps> = ({ canResolve = true }) => {
  const [filters, setFilters] = useState<FraudFlagFilters>({
    limit: 50,
    offset: 0,
  });
  const [selectedFlag, setSelectedFlag] = useState<FraudFlag | null>(null);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fmt = useFormatDate();
  const tableSort = useTableSort({
    columns: getTableSortColumns('fraudFlags'),
    onChange: () => {
      setFilters((prev) => ({ ...prev, offset: 0 }));
    },
  });

  useEffect(() => {
    setFilters((prev) => {
      const nextSortBy = tableSort.sortParams.sortBy;
      const nextSortOrder = tableSort.sortParams.sortOrder;
      if (prev.sortBy === nextSortBy && prev.sortOrder === nextSortOrder) {
        return prev;
      }
      const next: FraudFlagFilters = { ...prev, offset: 0 };
      if (nextSortBy && nextSortOrder) {
        next.sortBy = nextSortBy as FraudFlagFilters['sortBy'];
        next.sortOrder = nextSortOrder;
      } else {
        delete next.sortBy;
        delete next.sortOrder;
      }
      return next;
    });
  }, [tableSort.sortBy, tableSort.sortOrder]);

  const { data: fraudFlagsData, isLoading, isFetching, error, refetch } = useFraudFlags(filters);

  const handlePageChange = (newOffset: number) => {
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const handleFilterChange = (key: keyof FraudFlagFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handleResolve = (flag: FraudFlag) => {
    setSelectedFlag(flag);
    setIsResolveModalOpen(true);
  };

  const handleResolveSuccess = () => {
    setIsResolveModalOpen(false);
    setSelectedFlag(null);
    refetch();
  };

  const getFlagTypeLabel = (type: FraudFlagType) => {
    const labels: Record<FraudFlagType, string> = {
      high_deposit_volume: 'High Deposit Volume',
      rapid_deposit_withdrawal: 'Rapid Deposit-Withdrawal',
      high_deposit_frequency: 'High Deposit Frequency',
      abnormal_profit_ratio: 'Abnormal Profit Ratio',
      abnormal_absolute_profit: 'Abnormal Absolute Profit',
      new_account_high_profit: 'New Account High Profit',
      repeated_high_profit: 'Repeated High Profit',
    };
    return labels[type] || type;
  };

  const getSeverityBadge = (severity: FraudFlagSeverity) => {
    const config = {
      low: { label: 'Low', className: 'bg-blue-100 text-blue-800' },
      medium: { label: 'Medium', className: 'bg-yellow-100 text-yellow-800' },
      high: { label: 'High', className: 'bg-orange-100 text-orange-800' },
      critical: { label: 'Critical', className: 'bg-red-100 text-red-800' },
    };

    const severityConfig = config[severity] || config.low;
    return (
      <Badge className={severityConfig.className}>
        {severityConfig.label}
      </Badge>
    );
  };

  const handleExportConfirm = async () => {
    setIsExporting(true);
    try {
      const exportFilters: FraudFlagFilters = {
        ...filters,
        limit: EXPORT_LIMIT,
        offset: 0,
      };
      const res = await apiClient.getFraudFlags(exportFilters);
      const list = res?.data?.fraudFlags ?? [];
      const rows = list.map((f: FraudFlag) => ({
        'Transaction ID': f.transactionId,
        'User ID': f.userId,
        'Flag Type': getFlagTypeLabel(f.flagType),
        Severity: f.severity,
        Resolved: f.isResolved ? 'Yes' : 'No',
        'Resolved At': fmt(f.resolvedAt),
        'Created At': fmt(f.createdAt),
      }));
      exportToExcel(rows, {
        sheetName: 'Fraud Flags',
        fileName: `fraud-flags-export-${format(new Date(), 'yyyy-MM-dd')}`,
      });
      toast.success('Export completed successfully');
      setShowExportModal(false);
    } catch (err) {
      console.error('Export fraud flags error:', err);
      toast.error('Failed to export fraud flags. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Unable to load fraud flags</p>
          <Button onClick={() => refetch()} className="mt-4" variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const fraudFlags = fraudFlagsData?.data?.fraudFlags || [];
  const pagination = fraudFlagsData?.data?.pagination;

  const headerSort = tableSort.sortBy
    ? {
        activeKey: pagination?.sortBy ?? tableSort.sortBy,
        direction: (pagination?.sortOrder ?? tableSort.sortOrder) as SortDirection | null,
      }
    : { activeKey: null, direction: null };

  const isTableBusy = isFetching && fraudFlags.length > 0;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">User ID</label>
              <div className="relative">
                <Input
                  placeholder="Filter by user ID"
                  value={filters.userId || ''}
                  onChange={(e) => handleFilterChange('userId', e.target.value || undefined)}
                  className="pr-10"
                />
                {filters.userId && (
                  <button
                    onClick={() => handleFilterChange('userId', undefined)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Transaction ID</label>
              <div className="relative">
                <Input
                  placeholder="Filter by transaction ID"
                  value={filters.transactionId || ''}
                  onChange={(e) => handleFilterChange('transactionId', e.target.value || undefined)}
                  className="pr-10"
                />
                {filters.transactionId && (
                  <button
                    onClick={() => handleFilterChange('transactionId', undefined)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Flag Type</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.flagType || ''}
                onChange={(e) => handleFilterChange('flagType', e.target.value || undefined)}
              >
                <option value="">All Types</option>
                <option value="high_deposit_volume">High Deposit Volume</option>
                <option value="rapid_deposit_withdrawal">Rapid Deposit-Withdrawal</option>
                <option value="high_deposit_frequency">High Deposit Frequency</option>
                <option value="abnormal_profit_ratio">Abnormal Profit Ratio</option>
                <option value="abnormal_absolute_profit">Abnormal Absolute Profit</option>
                <option value="new_account_high_profit">New Account High Profit</option>
                <option value="repeated_high_profit">Repeated High Profit</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Severity</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.severity || ''}
                onChange={(e) => handleFilterChange('severity', e.target.value || undefined)}
              >
                <option value="">All Severities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.isResolved === undefined ? '' : filters.isResolved ? 'resolved' : 'unresolved'}
                onChange={(e) => {
                  const value = e.target.value;
                  handleFilterChange('isResolved', value === '' ? undefined : value === 'resolved');
                }}
              >
                <option value="">All Status</option>
                <option value="unresolved">Unresolved</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fraud Flags Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Fraud Flags</CardTitle>
            <div className="flex gap-2">
              <Button onClick={() => setShowExportModal(true)} variant="outline" size="sm" disabled={fraudFlags.length === 0}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to Excel
              </Button>
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : fraudFlags.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No fraud flags found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table aria-busy={isTableBusy || undefined}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>User ID</TableHead>
                      <TableHead>Flag Type</TableHead>
                      <SortableTableHead
                        sortable
                        label="Severity"
                        sortKey="severity"
                        activeKey={headerSort.activeKey}
                        direction={headerSort.direction}
                        onSort={tableSort.toggleSort}
                        disabled={isTableBusy}
                      />
                      <TableHead>Status</TableHead>
                      <SortableTableHead
                        sortable
                        label="Created At"
                        sortKey="createdAt"
                        activeKey={headerSort.activeKey}
                        direction={headerSort.direction}
                        onSort={tableSort.toggleSort}
                        disabled={isTableBusy}
                      />
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className={cn(isTableBusy && 'opacity-60 transition-opacity')}>
                    {fraudFlags.map((flag) => (
                      <TableRow key={flag.id}>
                        <TableCell className="font-mono text-sm">{flag.transactionId}</TableCell>
                        <TableCell className="font-mono text-sm">{flag.userId.substring(0, 8)}...</TableCell>
                        <TableCell>{getFlagTypeLabel(flag.flagType)}</TableCell>
                        <TableCell>{getSeverityBadge(flag.severity)}</TableCell>
                        <TableCell>
                          {flag.isResolved ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Resolved
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <XCircle className="h-3 w-3 mr-1" />
                              Unresolved
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{fmt(flag.createdAt)}</TableCell>
                        <TableCell>
                          {!flag.isResolved && canResolve && (
                            <Button
                              onClick={() => handleResolve(flag)}
                              variant="outline"
                              size="sm"
                            >
                              Resolve
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination && pagination.total > 0 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-600">
                    Showing {(filters.offset || 0) + 1} to {Math.min((filters.offset || 0) + (filters.limit || 50), pagination.total)} of {pagination.total} flags
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handlePageChange(Math.max(0, (filters.offset || 0) - (filters.limit || 50)))}
                      disabled={!pagination.hasMore || (filters.offset || 0) === 0}
                      variant="outline"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => handlePageChange((filters.offset || 0) + (filters.limit || 50))}
                      disabled={!pagination.hasMore}
                      variant="outline"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Resolve Modal */}
      {selectedFlag && (
        <ResolveFraudFlagModal
          flag={selectedFlag}
          isOpen={isResolveModalOpen}
          onClose={() => {
            setIsResolveModalOpen(false);
            setSelectedFlag(null);
          }}
          onSuccess={handleResolveSuccess}
        />
      )}

      <ExportConfirmModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        isExporting={isExporting}
        title="Export to Excel"
        message="Only the latest 1000 records will be exported. Do you want to continue?"
      />
    </div>
  );
};

export default FraudFlagsList;

